// The small helpers more than one map layer needs (§10.4).
//
// Nothing here allocates on a frame: `grown` and `growColumns` run only
// when a column set doubles, `cellsOf` runs when a mask lands, `hexOutline`
// runs when a cell changes, and the rest write into what the caller hands
// in or answer one number. The hidden-document check the layers also share
// is `hiddenDocument` of `render/motion.ts`.

import { CULL_MARGIN_PX, type ViewRect } from '../layer';
import { hexCorner } from '../../world';
import type { SegmentSink } from './batches';

/** Every typed array a layer keeps a column in. */
type Column = Uint8Array | Uint16Array | Uint32Array | Float32Array | Float64Array;

/**
 * A copy of one column at a new capacity, `per` numbers a slot.
 *
 * A structure of arrays doubles every column together, so each layer states
 * its columns once and this states the copy once.
 */
export function grown<T extends Column>(a: T, cap: number, per = 1): T {
  const b = new (a.constructor as new (n: number) => T)(cap * per);
  b.set(a);
  return b;
}

/**
 * Grows every column a layer names to `cap` slots.
 *
 * A layer keeps its columns as fields and names them ONCE, in a list that
 * this walk and `moveSlot` share, so the grow and the swap-remove can never
 * name two different sets (R2212). A column with more than one number a
 * slot is grown and moved by its layer itself.
 */
export function growColumns(owner: object, names: readonly string[], cap: number): void {
  const o = owner as Record<string, Column>;
  for (let i = 0; i < names.length; i++) o[names[i]] = grown(o[names[i]], cap);
}

/**
 * Copies slot `from` of every column a layer names into slot `to`. A plain
 * array of one entry a slot moves the same way, so a layer may name its
 * key and label lists here too.
 */
export function moveSlot(owner: object, names: readonly string[], to: number, from: number): void {
  const o = owner as Record<string, { [slot: number]: unknown }>;
  for (let i = 0; i < names.length; i++) {
    const a = o[names[i]];
    a[to] = a[from];
  }
}

/** Every cell of `mask` that holds `kind`, in index order. A null mask holds none. */
export function cellsOf(mask: Uint8Array | null, kind: number): Int32Array {
  const list: number[] = [];
  if (mask) {
    for (let i = 0; i < mask.length; i++) if (mask[i] === kind) list.push(i);
  }
  return Int32Array.from(list);
}

/** Writes `view` grown by the shared cull margin into `out`, and returns it. */
export function grownView(out: ViewRect, view: ViewRect): ViewRect {
  out.x0 = view.x0 - CULL_MARGIN_PX; out.x1 = view.x1 + CULL_MARGIN_PX;
  out.y0 = view.y0 - CULL_MARGIN_PX; out.y1 = view.y1 + CULL_MARGIN_PX;
  return out;
}

/**
 * How much a mark shrinks at `zoom` under the hold of R552: 1 on the live
 * map (`holdZoom` 0), and `holdZoom / zoom` once the camera is in past
 * `holdZoom`, so the mark keeps the screen size it had there. The replay
 * film sets the hold of the gun shots and of the blasts.
 */
export function holdScale(holdZoom: number, zoom: number): number {
  return holdZoom > 0 && zoom > holdZoom ? holdZoom / zoom : 1;
}

/** The camera zoom a screen-px figure divides by. A zero zoom answers 1. */
export function zoomOf(zoom: number): number {
  return zoom > 1e-6 ? zoom : 1;
}

/** The four corners of a bracket, as unit offsets. */
const CORNERS: readonly (readonly [number, number])[] = [[-1, -1], [1, -1], [-1, 1], [1, 1]];

/**
 * The four corner brackets round `(x, y)`: at each corner of the square of
 * half side `r`, one arm of length `arm` along y and one along x. The
 * selection bracket of a hull, the target bracket of a sea object and the
 * bracket of a staked contest draw this way.
 */
export function cornerBrackets(
  s: Pick<SegmentSink, 'line'>, x: number, y: number, r: number, arm: number,
  width: number, color: number, alpha: number, flags = 0,
): void {
  for (const [ux, uy] of CORNERS) {
    s.line(x + ux * r, y + uy * r - uy * arm, x + ux * r, y + uy * r, width, color, alpha, flags);
    s.line(x + ux * r, y + uy * r, x + ux * r - ux * arm, y + uy * r, width, color, alpha, flags);
  }
}

/**
 * The six sides of the map's hex round the cell centre `(cx, cy)`, one line
 * a side. The drill spot and the hover cell outline a cell this way.
 */
export function hexOutline(
  s: SegmentSink, cx: number, cy: number, width: number, color: number, alpha: number,
  flags: number, dashOn: number, dashOff: number,
): void {
  for (let k = 0; k < 6; k++) {
    const [ax, ay] = hexCorner(k);
    const [bx, by] = hexCorner((k + 1) % 6);
    s.line(cx + ax, cy + ay, cx + bx, cy + by, width, color, alpha, flags, dashOn, dashOff);
  }
}
